import { AxiosRequestConfig, AxiosResponse } from "axios";

import { Paths } from "./paths";
import { removeSession } from "../utils";
import { apiErrorCode, $axios, API_URL } from "./axios.instance";

/**
 * Shape of an error response returned by the API layer.
 * It keeps the structure flexible while documenting the most common fields.
 */
export interface ApiErrorResponse {
  statusCode?: number | string;
  message?: string;
  data?: {
    statusCode?: number | string;
    message?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export type ApiSuccessCallback<T = unknown> = (
  response: AxiosResponse<T>,
) => void;
export type ApiErrorCallback = (error: ApiErrorResponse) => void;

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  errorCallback?: ApiErrorCallback;
  customHeaders?: Record<string, string>;
  skipAuth?: boolean;
}
/**
 * Returns true when the supplied error object indicates an invalid session.
 */
export const checkUserValidation = (data: ApiErrorResponse) => {
  if (data?.statusCode) {
    const { statusCode: stc } = data;
    return (
      stc === apiErrorCode.INVALID_TOKEN || stc === apiErrorCode.ACCOUNT_BLOCKED
    );
  }
  return false;
};

/**
 * Returns true when the supplied error object indicates an expired session.
 */
export const checkUserSessionValidation = (data: ApiErrorResponse) => {
  if (data?.statusCode) {
    const { statusCode: stc } = data;
    return stc === apiErrorCode.SESSION_EXPIRED;
  }
  return false;
};

/**
 * Handles axios errors and forwards structured error information to the provided callback.
 * Consumer specific flows (session invalidation, forced reload, etc.) live here to remain consistent.
 */
export const handleApiError = (
  error: unknown,
  errorCallback: ApiErrorCallback,
) => {
  if (typeof error === "object" && error !== null) {
    const networkError = error as { code?: string; message?: string };
    if (networkError.code === "ERR_NETWORK") {
      errorCallback({
        statusCode: "",
        message: networkError.message,
      });
      return;
    }

    if (networkError.code === "ECONNABORTED") {
      errorCallback({
        data: {
          statusCode: 408,
        },
      });
      return;
    }

    const responseError = error as {
      response?: { status?: number; data?: ApiErrorResponse };
    };

    if (responseError.response?.data) {
      const { status, data } = responseError.response;

      if (
        status === apiErrorCode.USER_DEACTIVATED &&
        data.statusCode === apiErrorCode.USER_DEACTIVATED
      ) {
        errorCallback(data);
        localStorage.clear();

        if (
          !window.location.pathname.includes(Paths.SIGNIN) &&
          !window.location.pathname.includes(Paths.SIGNIN_EMAIL)
        ) {
          window.location.reload();
        }
        return;
      }

      if (checkUserValidation(data)) {
        removeSession();
        errorCallback(data);
        return;
      }

      if (checkUserSessionValidation(data)) {
        const errorData: ApiErrorResponse = {
          ...data,
          message: "Your Session has been Expired",
        };
        removeSession();
        errorCallback(errorData);
        return;
      }

      errorCallback(data);
      return;
    }
  }

  errorCallback({
    data: {
      statusCode: "",
      message: "Please try again later",
    },
  });
};

/**
 * Creates a minimal axios config that allows interceptors to access the error callback.
 */
const buildConfig = (
  errorCallback: ApiErrorCallback,
  customHeaders?: Record<string, string>,
): CustomAxiosRequestConfig => ({
  errorCallback,
  customHeaders,
});

/**
 * Passes a request promise through to the success callback while leaving error handling
 * to the shared axios interceptors.
 */
const forwardResponse = <T>(
  request: Promise<AxiosResponse<T>>,
  successCallback: ApiSuccessCallback<T>,
) => {
  request.then(successCallback).catch(() => {
    /* Errors are handled globally via interceptors */
  });
};

/**
 * Executes an axios GET request with shared configuration and success forwarding.
 */
const getApiCall = <T = unknown>(
  endPoint: string,
  params: string,
  successCallback: ApiSuccessCallback<T>,
  errorCallback: ApiErrorCallback,
  customHeaders?: Record<string, string>,
) => {
  const config = buildConfig(errorCallback, customHeaders);
  forwardResponse(
    $axios.get<T>(API_URL + endPoint + params, config),
    successCallback,
  );
};

/**
 * Executes an axios POST request with shared configuration and success forwarding.
 */
const postApiCall = <T = unknown, P = Record<string, unknown>>(
  endPoint: string,
  dataToSend: P,
  successCallback: ApiSuccessCallback<T>,
  errorCallback: ApiErrorCallback,
  customHeaders?: Record<string, string>,
) => {
  const config = buildConfig(errorCallback, customHeaders);

  forwardResponse(
    $axios.post<T>(API_URL + endPoint, dataToSend, config),
    successCallback,
  );
};

/**
 * Executes an axios PUT request with shared configuration and success forwarding.
 */
const putApiCall = <T = unknown, P = Record<string, unknown>>(
  endPoint: string,
  params: P,
  successCallback: ApiSuccessCallback<T>,
  errorCallback: ApiErrorCallback,
  customHeaders?: Record<string, string>,
) => {
  const config = buildConfig(errorCallback, customHeaders);
  forwardResponse(
    $axios.put<T>(API_URL + endPoint, params, config),
    successCallback,
  );
};

/**
 * Executes an axios PATCH request with shared configuration and success forwarding.
 */
const patchApiCall = <T = unknown, P = Record<string, unknown> | string>(
  endPoint: string,
  params: P,
  successCallback: ApiSuccessCallback<T>,
  errorCallback: ApiErrorCallback,
  customHeaders?: Record<string, string>,
) => {
  const config = buildConfig(errorCallback, customHeaders);
  forwardResponse(
    $axios.patch<T>(API_URL + endPoint, params, config),
    successCallback,
  );
};

/**
 * Executes an axios DELETE request with shared configuration and success forwarding.
 */
const deleteApiCall = <T = unknown, P = string>(
  endPoint: string,
  params: P,
  successCallback: ApiSuccessCallback<T>,
  errorCallback: ApiErrorCallback,
  customHeaders?: Record<string, string>,
) => {
  const config = buildConfig(errorCallback, customHeaders);
  forwardResponse(
    $axios.delete<T>(API_URL + endPoint + params, config),
    successCallback,
  );
};

export { getApiCall, postApiCall, putApiCall, patchApiCall, deleteApiCall };
